import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Boxes, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import StatusChip from "../../components/system/StatusChip";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorBatchesPage — `/platform/companies/:cid/batches`
 *
 * Active batches table with filters. Query-string driven:
 *   ?status=active|removed   (default active)
 *   ?store=<storeId>
 *   ?department=<deptId>
 *   ?q=<freetext>
 *
 * Phase 1: READ-ONLY.
 */
function expirationTone(dateStr) {
  if (!dateStr) return "neutral";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr);
  if (Number.isNaN(exp.getTime())) return "neutral";
  const diffDays = Math.round((exp - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays <= 3) return "urgent";
  if (diffDays <= 7) return "soon";
  return "fresh";
}

function expirationLabel(dateStr) {
  if (!dateStr) return "—";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr);
  if (Number.isNaN(exp.getTime())) return dateStr;
  const diffDays = Math.round((exp - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${Math.abs(diffDays)}d ago`;
  if (diffDays === 0) return "Today";
  return `${diffDays}d`;
}

export default function OperatorBatchesPage() {
  const { cid } = useParams();
  const [params, setParams] = useSearchParams();
  const [companyName, setCompanyName] = useState("");
  const [batches, setBatches] = useState([]);
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const status = params.get("status") || "active";
  const storeId = params.get("store") || "";
  const deptId = params.get("department") || "";
  const q = params.get("q") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const queryParts = [`status=${encodeURIComponent(status)}`, "limit=500"];
      if (storeId) queryParts.push(`store_id=${encodeURIComponent(storeId)}`);
      if (deptId)
        queryParts.push(`department_id=${encodeURIComponent(deptId)}`);
      const queryString = queryParts.join("&");
      const [det, b, st, dp] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/batches?${queryString}`),
        api.get(`/platform/companies/${cid}/stores`),
        api.get(`/platform/companies/${cid}/departments`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setBatches(b.data.batches || []);
      setStores(st.data.stores || []);
      setDepartments(dp.data.departments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid, status, storeId, deptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id) => map.get(id) || "—";
  }, [stores]);
  const deptName = useMemo(() => {
    const map = new Map(departments.map((d) => [d.id, d.name]));
    return (id) => map.get(id) || "—";
  }, [departments]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return batches;
    return batches.filter((b) => {
      const hay =
        (b.product_name || "") +
        " " +
        (b.product_barcode || "") +
        " " +
        (b.batch_code || "");
      return hay.toLowerCase().includes(term);
    });
  }, [batches, q]);

  const GRID =
    "minmax(0,2fr) 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

  return (
    <div data-testid="operator-batches-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-batches-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title={status === "removed" ? "Removed batches" : "Active batches"}
        subtitle={`${filtered.length} batch${filtered.length === 1 ? "" : "es"} matching filters.`}
      />

      <Card variant="raised" className="p-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative lg:col-span-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Product, barcode, batch code…"
              className="pl-9"
              data-testid="operator-batches-search"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setFilter("status", e.target.value)}
            data-testid="operator-batches-filter-status"
          >
            <option value="active">Active</option>
            <option value="removed">Removed</option>
          </Select>
          <Select
            value={storeId}
            onChange={(e) => setFilter("store", e.target.value)}
            data-testid="operator-batches-filter-store"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Select
            value={deptId}
            onChange={(e) => setFilter("department", e.target.value)}
            data-testid="operator-batches-filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
          {(q || storeId || deptId || status !== "active") && (
            <Button
              tier="tertiary"
              size="sm"
              onClick={() =>
                setParams({ status: "active" }, { replace: true })
              }
              data-testid="operator-batches-clear-filters"
            >
              Reset filters
            </Button>
          )}
        </div>
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Product</div>
            <div>Barcode</div>
            <div>Store</div>
            <div>Department</div>
            <div className="text-right">Qty</div>
            <div>Expiration</div>
            <div>Added by</div>
            <div>Created</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<Boxes size={32} />}
              title="No batches match"
              description="Adjust filters or change status to see other batches."
              data-testid="operator-batches-empty"
            />
          </div>
        }
        data-testid="operator-batches-table"
      >
        {filtered.map((b) => (
          <OperatorTableRow
            key={b.id}
            gridTemplate={GRID}
            data-testid={`operator-batch-row-${b.id}`}
          >
            <div className="min-w-0">
              <div className="font-display font-semibold truncate">
                {b.product_name || "—"}
              </div>
              {b.batch_code && (
                <div className="font-mono text-[11px] text-muted-foreground truncate">
                  batch {b.batch_code}
                </div>
              )}
            </div>
            <div className="font-mono text-xs truncate">
              {b.product_barcode || "—"}
            </div>
            <div className="font-mono text-xs truncate">
              {storeName(b.store_id)}
            </div>
            <div className="font-mono text-xs truncate">
              {deptName(b.department_id)}
            </div>
            <div className="text-right font-mono tabular-nums">
              {b.quantity ?? 0}
            </div>
            <div>
              <StatusChip tone={expirationTone(b.expiration_date)}>
                {expirationLabel(b.expiration_date)}
              </StatusChip>
            </div>
            <div className="font-mono text-[11px] truncate">
              {b.added_by_email || b.added_by_name || "—"}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              {(b.created_at || "").slice(0, 10) || "—"}
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>
    </div>
  );
}
